import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { LogOut, History, Calendar, TrendingUp, Target, User, Heart, Sparkles } from 'lucide-react';
import DailyQuote from '@/components/DailyQuote';
import StreakDisplay from '@/components/StreakDisplay';
import XPProgressBar from '@/components/XPProgressBar';
import ChallengeCard from '@/components/ChallengeCard';
import AIInsightCard from '@/components/AIInsightCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const moods = [
  { value: 'awesome', emoji: '😄', label: 'Harika', color: '#fef3c7', textColor: '#92400e' },
  { value: 'good', emoji: '🙂', label: 'İyi', color: '#d1fae5', textColor: '#065f46' },
  { value: 'okay', emoji: '😐', label: 'Normal', color: '#dbeafe', textColor: '#1e40af' },
  { value: 'bad', emoji: '😔', label: 'Kötü', color: '#fce7f3', textColor: '#9f1239' },
  { value: 'awful', emoji: '😢', label: 'Çok Kötü', color: '#f3e8ff', textColor: '#6b21a8' },
];

const timeOfDayOptions = [
  { value: 'morning', label: 'Sabah', emoji: '🌅' },
  { value: 'afternoon', label: 'Öğlen', emoji: '☀️' },
  { value: 'evening', label: 'Akşam', emoji: '🌆' },
  { value: 'night', label: 'Gece', emoji: '🌙' },
  { value: 'anytime', label: 'Belirtilmemiş', emoji: '⏰' },
];

export default function Dashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [selectedMood, setSelectedMood] = useState(null);
  const [intensity, setIntensity] = useState(5);
  const [timeOfDay, setTimeOfDay] = useState('anytime');
  const [note, setNote] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [todayMood, setTodayMood] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [wiggle, setWiggle] = useState(false);
  const [profile, setProfile] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);

  useEffect(() => {
    fetchTodayMood();
    fetchStatistics();
    fetchProfile();
    fetchChallenges();
  }, [selectedDate]);

  const fetchStatistics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/moods/statistics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatistics(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchChallenges = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/challenges/daily`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChallenges(response.data.challenges.filter(c => !c.completed).slice(0, 2));
    } catch (error) {
      console.error('Error fetching challenges:', error);
    }
  };

  const fetchAIAnalysis = async () => {
    setLoadingAI(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/ai/analyze`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAiAnalysis(response.data);
    } catch (error) {
      toast.error('AI analizi yapılamadı');
    } finally {
      setLoadingAI(false);
    }
  };

  const handleCompleteChallenge = async (challengeId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/challenges/${challengeId}/complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`🎉 ${response.data.message} +${response.data.xp_earned} XP!`);
      fetchChallenges();
      fetchProfile();
    } catch (error) {
      const message = error.response?.data?.message || 'Bir hata oluştu';
      toast.error(message);
    }
  };

  const fetchTodayMood = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/moods`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const mood = response.data.find(m => m.date === selectedDate);
      if (mood) {
        setTodayMood(mood);
        setSelectedMood(mood.mood);
        setIntensity(mood.intensity || 5);
        setTimeOfDay(mood.time_of_day || 'anytime');
        setNote(mood.note || '');
      } else {
        setTodayMood(null);
        setSelectedMood(null);
        setIntensity(5);
        setTimeOfDay('anytime');
        setNote('');
      }
    } catch (error) {
      console.error('Error fetching mood:', error);
    }
  };

  const handleMoodSelect = (moodValue) => {
    setSelectedMood(moodValue);
    setWiggle(true);
    setTimeout(() => setWiggle(false), 500);
  };

  const handleSaveMood = async () => {
    if (!selectedMood) {
      toast.error('Lütfen bir ruh hali seçin');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (todayMood) {
        await axios.put(
          `${API}/moods/${todayMood.id}`,
          { mood: selectedMood, intensity, time_of_day: timeOfDay, note, date: selectedDate },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Ruh haliniz güncellendi! 🎉');
      } else {
        await axios.post(
          `${API}/moods`,
          { mood: selectedMood, intensity, time_of_day: timeOfDay, note, date: selectedDate },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Ruh haliniz kaydedildi! 🎉');
      }
      
      fetchTodayMood();
      fetchStatistics();
    } catch (error) {
      const message = error.response?.data?.detail || 'Bir hata oluştu';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMood = async () => {
    if (!todayMood) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/moods/${todayMood.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ruh hali kaydı silindi');
      setTodayMood(null);
      setSelectedMood(null);
      setIntensity(5);
      setTimeOfDay('anytime');
      setNote('');
      fetchStatistics();
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  const getIntensityLabel = (value) => {
    if (value <= 2) return 'Çok düşük';
    if (value <= 4) return 'Düşük';
    if (value <= 6) return 'Orta';
    if (value <= 8) return 'Yüksek';
    return 'Çok yüksek';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <span className="text-3xl animate-float">😊</span>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">MoodPulse</h1>
              <p className="text-sm text-gray-600">Hoş geldin, {user.username}!</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="profile-btn"
              variant="outline"
              onClick={() => navigate('/profile')}
              className="gap-2 hover-lift"
            >
              <User className="w-4 h-4" />
              Profil
            </Button>
            <Button
              data-testid="challenges-btn"
              variant="outline"
              onClick={() => navigate('/challenges')}
              className="gap-2 hover-lift"
            >
              <Target className="w-4 h-4" />
              Görevler
            </Button>
            <Button
              data-testid="wellness-btn"
              variant="outline"
              onClick={() => navigate('/wellness')}
              className="gap-2 hover-lift"
            >
              <Heart className="w-4 h-4" />
              Wellness
            </Button>
            <Button
              data-testid="statistics-btn"
              variant="outline"
              onClick={() => navigate('/statistics')}
              className="gap-2 hover-lift"
            >
              <TrendingUp className="w-4 h-4" />
              İstatistikler
            </Button>
            <Button
              data-testid="history-btn"
              variant="outline"
              onClick={() => navigate('/history')}
              className="gap-2 hover-lift"
            >
              <History className="w-4 h-4" />
              Geçmiş
            </Button>
            <Button
              data-testid="logout-btn"
              variant="outline"
              onClick={onLogout}
              className="gap-2 hover-lift"
            >
              <LogOut className="w-4 h-4" />
              Çıkış
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* XP Progress */}
          {profile && (
            <div className="lg:col-span-3">
              <XPProgressBar 
                level={profile.level}
                xp={profile.xp}
                xpForNextLevel={profile.xp_for_next_level}
              />
            </div>
          )}

          {/* Streak Display */}
          {statistics && (
            <div className="lg:col-span-3">
              <StreakDisplay 
                currentStreak={statistics.current_streak}
                longestStreak={statistics.longest_streak}
                totalEntries={statistics.total_entries}
              />
            </div>
          )}

          {/* Daily Quote */}
          <div className="lg:col-span-2">
            <DailyQuote />
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-2xl p-6 shadow-lg hover-lift animate-scale-in">
            <h3 className="text-lg font-semibold mb-4">Hızlı Erişim</h3>
            <div className="space-y-2">
              <Button
                onClick={() => navigate('/challenges')}
                variant="outline"
                className="w-full justify-start gap-2"
              >
                <Target className="w-4 h-4" />
                Görevler ({challenges.length} aktif)
              </Button>
              <Button
                onClick={() => navigate('/wellness')}
                variant="outline"
                className="w-full justify-start gap-2"
              >
                <Heart className="w-4 h-4" />
                Wellness & Meditasyon
              </Button>
              <Button
                onClick={fetchAIAnalysis}
                disabled={loadingAI}
                variant="outline"
                className="w-full justify-start gap-2"
              >
                <Sparkles className="w-4 h-4" />
                {loadingAI ? 'Analiz ediliyor...' : 'AI Analizi'}
              </Button>
            </div>
          </div>
        </div>

        {/* Active Challenges */}
        {challenges.length > 0 && (
          <div className="mb-6">
            <h3 className="text-xl font-semibold mb-4">🎯 Aktif Görevler</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {challenges.map((challenge) => (
                <ChallengeCard
                  key={challenge.id}
                  challenge={challenge}
                  onComplete={handleCompleteChallenge}
                  loading={false}
                />
              ))}
            </div>
          </div>
        )}

        {/* AI Analysis */}
        {aiAnalysis && (
          <div className="mb-6">
            <AIInsightCard
              analysis={aiAnalysis.analysis}
              suggestions={aiAnalysis.suggestions}
              trend={aiAnalysis.mood_trend}
            />
          </div>
        )}

        <div className="bg-white rounded-3xl shadow-xl p-8 animate-slide-up">
          {/* Date Selector */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="inline w-4 h-4 mr-1" />
              Tarih
            </label>
            <input
              data-testid="date-input"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
            />
          </div>

          {/* Mood Selector */}
          <div className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Bugün nasıl hissediyorsun?
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              {moods.map((mood) => (
                <button
                  key={mood.value}
                  data-testid={`mood-${mood.value}-btn`}
                  onClick={() => handleMoodSelect(mood.value)}
                  className={`p-6 rounded-2xl border-2 hover-lift emoji-hover ${
                    selectedMood === mood.value ? 'emoji-selected' : ''
                  } ${wiggle && selectedMood === mood.value ? 'animate-wiggle' : ''}`}
                  style={{
                    backgroundColor: selectedMood === mood.value ? mood.color : 'white',
                    borderColor: selectedMood === mood.value ? mood.textColor : '#e5e7eb',
                    color: selectedMood === mood.value ? mood.textColor : '#6b7280'
                  }}
                >
                  <div className="text-5xl mb-2">{mood.emoji}</div>
                  <div className="font-medium">{mood.label}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Intensity Slider */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Yoğunluk: <span className="text-purple-600 font-semibold">{intensity}/10</span>
              <span className="ml-2 text-xs text-gray-500">({getIntensityLabel(intensity)})</span>
            </label>
            <input
              data-testid="intensity-slider"
              type="range"
              min="1"
              max="10"
              value={intensity}
              onChange={(e) => setIntensity(parseInt(e.target.value))}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>1</span>
              <span>5</span>
              <span>10</span>
            </div>
          </div>

          {/* Time of Day */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Günün saati
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {timeOfDayOptions.map((time) => (
                <button
                  key={time.value}
                  data-testid={`time-${time.value}-btn`}
                  onClick={() => setTimeOfDay(time.value)}
                  className={`p-3 rounded-xl border-2 transition-all hover-grow ${
                    timeOfDay === time.value
                      ? 'bg-purple-100 border-purple-500 text-purple-900'
                      : 'bg-white border-gray-200 text-gray-700 hover:border-purple-300'
                  }`}
                >
                  <div className="text-2xl mb-1">{time.emoji}</div>
                  <div className="text-sm font-medium">{time.label}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Note Input */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Not (İsteğe bağlı)
            </label>
            <Textarea
              data-testid="note-input"
              placeholder="Bugün neler oldu? Nasıl hissettiniz?"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              className="w-full resize-none transition-all focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              data-testid="save-mood-btn"
              onClick={handleSaveMood}
              disabled={loading || !selectedMood}
              className="flex-1 py-6 text-lg font-semibold hover-lift"
            >
              {loading ? 'Kaydediliyor...' : todayMood ? '🔄 Güncelle' : '💾 Kaydet'}
            </Button>
            {todayMood && (
              <Button
                data-testid="delete-mood-btn"
                onClick={handleDeleteMood}
                variant="destructive"
                className="py-6 text-lg font-semibold hover-lift"
              >
                🗑️ Sil
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}